import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter,Input, Button, ButtonGroup, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { generateClassNames } from '../utils/generateClassNames'
import moment from 'moment-timezone';
import './ProfileSettings.css';
import SpinnerCircles from "./Spinner/SpinerCircles";

const RadioButtonGroup = ({ selected, setSelected, options }) => {
    return (
        <div className="profile-settings-radio-btn-group">
            {options.map((option, index) => (
                <div key={index} className="profile-settings-radio-btn-group-container">
                    <Input
                        type="radio"
                        id={`radio-${option.value}`}
                        name="timezone-options"
                        value={option.value}
                        checked={selected?.value === option.value}
                        onChange={() => setSelected(option)}
                        className={generateClassNames('profile-settings-radio-btn', [[selected?.value === option.value, 'selected']])}
                    />
                    <label htmlFor={`radio-${option.value}`} className='profile-settings-radio-btn-label'>{option.label}</label>
                </div>
            ))}
        </div>
    );
};

const CustomDropdown = ({ disabled, label, menuItems, direction = 'down', dropdownWidth = '85%', onSelect, selectedValue }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

    return (
        <Dropdown className='custom-dropdown-container' isOpen={dropdownOpen} toggle={toggleDropdown} direction={direction}>
            <DropdownToggle disabled={disabled} caret style={{ width: dropdownWidth, display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                background: 'white',
                boxShadow: 'none',
                borderRadius: 4,
                border: '1px solid #595555',
                margin: 0,
                color: '#3F4254',
                fontWeight: 600
            }}>
                {label}
            </DropdownToggle>
            <DropdownMenu style={{
                width: dropdownWidth,
                padding: '7px 5px',
                margin: 0,
                borderRadius: 0,
                maxHeight: '200px',
                overflowY: 'auto'
            }}>
                {menuItems.map((item, index) => (
                    <DropdownItem 
                        key={index} 
                        onClick={() => onSelect(item)}
                        active={selectedValue?.value === item.value}
                    >
                        {item.label}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    );
};

const default2FaOptions = [
    { label: "Authentication App", value: '2factorapp' },
    { label: "SMS", value: 'sms' },
    { label: "EMAIL", value: 'email' }
];

const timeZoneOptions = [
    { label: `Browser (Default)`, value: 'browser' },
    { label: "Select TimeZone", value: 'custom' }
];



const ProfileSettings = ({ isOpen, handleCancel, isUserProfileSettingsMutating, saveProfileSettings, default2FaMethod = 'email', userTimezone = 'browser' }) => {
    const [rSelected, setRSelected] = useState(null);

    const [timeZones, setTimeZones] = useState([]);

    const [default2FaOption, setDefault2FaOption] = useState(null);
    const [selectedTimeZone, setSelectedTimeZone] = useState(null);


    useEffect(() => {
        const zones = moment.tz.names().map(zone => ({ label: zone, value: zone }));
        setTimeZones(zones);
    }, []);

    useEffect(() => {
        if(userTimezone && userTimezone !== 'browser'){
            const timeZone = timeZones.find(tmz => tmz?.value === userTimezone)
            if(timeZone){
                setSelectedTimeZone(timeZone)
            } else {
                setSelectedTimeZone(timeZones[0])
                setRSelected(timeZoneOptions[1])
            }
        } else {
            setRSelected(timeZoneOptions[0])   
            setSelectedTimeZone(timeZoneOptions[0])
        }       
    }, [userTimezone, timeZones])

    useEffect(() => {
      if(default2FaMethod) {
        const def2fa = default2FaOptions.find(dfa => dfa?.value === default2FaMethod)
        setDefault2FaOption(def2fa)
      } else {
        setDefault2FaOption(default2FaOptions[2])
      }
    }, [default2FaMethod])

    const handleRSelected = (selectedOption) => {
      if(selectedOption?.value === 'custom') {
        if(selectedTimeZone === null && !userTimezone){
            setSelectedTimeZone(timeZones[0])
        } else if(userTimezone) {
            const timeZone = timeZones.find(tmz => tmz?.value === userTimezone)
            timeZone ? setSelectedTimeZone(timeZone) : setSelectedTimeZone(timeZones[0])
        }
      } else if (selectedOption?.value === 'browser'){
        setSelectedTimeZone(timeZoneOptions[0])
      }
      setRSelected(selectedOption)
    }

    const handleSaveSettings = () => {
        saveProfileSettings({ default2Fa: default2FaOption?.value, timeZone: selectedTimeZone })
    }
    
    return (
        <div>
            <Modal isOpen={isOpen} size='lg'>
                <ModalHeader className='profile-settings-header'>Profile Settings</ModalHeader>
                <ModalBody  className='profile-settings-body'>
                    <div className='profile-settings-timezone'>
                        <div className='profile-settings-title'>User TimeZone</div>
                            <RadioButtonGroup
                                selected={rSelected}
                                setSelected={handleRSelected}
                                options={timeZoneOptions}
                            />
                            <CustomDropdown
                                disabled={!(rSelected?.value === 'custom')}
                                label={(rSelected?.value === 'custom' &&  selectedTimeZone?.value !== 'browser') ? selectedTimeZone?.label : (rSelected?.value === 'custom' && selectedTimeZone?.value === null) ? timeZones[0]?.label : 'Select TimeZone' }
                                menuItems={timeZones}
                                direction="end"
                                onSelect={setSelectedTimeZone}
                                selectedValue={selectedTimeZone === null ? timeZones[0] : selectedTimeZone}
                            />
                        </div>
                    <div className='profile-settings-default2fa'>
                        <div className='profile-settings-title'>Default 2FA Method</div>
                            <div className='profile-settings-default2fa-radio-btn-container'>
                                <span className='profile-settings-radio-btn-label' style={{ marginLeft: '0px' }}>Default 2FA Method</span>
                            </div>
                            <CustomDropdown
                                label={default2FaOption?.label}
                                menuItems={default2FaOptions}
                                direction="end"
                                onSelect={setDefault2FaOption}
                                selectedValue={default2FaOption}
                            />
                    </div>
                </ModalBody>
                <ModalFooter className='profile-settings-footer'>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button style={{ width: isUserProfileSettingsMutating ?  '120px'  :  'fit-content' }} color="primary" onClick={handleSaveSettings}> {isUserProfileSettingsMutating ? <SpinnerCircles /> : 'Save Changes'} </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default ProfileSettings;